import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState, useEffect } from "react";
import { Shield, CheckCircle2, Circle, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";

const GPHC_STANDARDS = [
  {
    num: 1,
    title: "Provide person-centred care",
    desc: "You put the person you are providing care to at the centre of your practice. You work in partnership with them to make decisions about their care.",
  },
  {
    num: 2,
    title: "Work in partnership with others",
    desc: "You work collaboratively and co-operate with colleagues and other healthcare professionals in the interest of people receiving care.",
  },
  {
    num: 3,
    title: "Communicate effectively",
    desc: "You communicate clearly and effectively with people receiving care and with colleagues.",
  },
  {
    num: 4,
    title: "Maintain, develop and use your professional knowledge and skills",
    desc: "You maintain and develop your professional knowledge and skills, including keeping up to date with developments in pharmacy practice.",
  },
  {
    num: 5,
    title: "Use professional judgement",
    desc: "You use your professional knowledge, skills and judgement to make decisions about care.",
  },
  {
    num: 6,
    title: "Behave in a professional manner",
    desc: "You act in a professional manner and behave in ways that are consistent with membership of the pharmacy profession.",
  },
  {
    num: 7,
    title: "Respect and maintain the person's confidentiality and privacy",
    desc: "You manage information about people confidentially, securely and appropriately.",
  },
  {
    num: 8,
    title: "Speak up when things go wrong",
    desc: "You are open and honest when things go wrong. You take action and encourage others to do the same.",
  },
  {
    num: 9,
    title: "Demonstrate leadership",
    desc: "You demonstrate leadership by contributing to the development and management of pharmacy services.",
  },
];

const STORAGE_KEY = "rheumhub_pharmacist_revalidation";

function getRenewalDate(): Date {
  const stored = localStorage.getItem(STORAGE_KEY + "_renewal");
  if (stored) return new Date(stored);
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d;
}

function getDaysUntil(d: Date): number {
  const now = new Date();
  return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export default function PharmacistRevalidation() {
  const [renewalDate, setRenewalDate] = useState<Date>(getRenewalDate);
  const [renewalInput, setRenewalInput] = useState(renewalDate.toISOString().split("T")[0]);
  const [peerDone, setPeerDone] = useState(() => localStorage.getItem(STORAGE_KEY + "_peer") === "true");
  const [reflectiveDone, setReflectiveDone] = useState(() => localStorage.getItem(STORAGE_KEY + "_reflective") === "true");
  const [cpdCount, setCpdCount] = useState<number>(0);
  const [expandedStd, setExpandedStd] = useState<number | null>(null);

  useEffect(() => {
    fetch("/api/pharmacist/cpd/summary")
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setCpdCount(data.count); })
      .catch(() => {});
  }, []);

  const savePeer = (v: boolean) => {
    setPeerDone(v);
    localStorage.setItem(STORAGE_KEY + "_peer", String(v));
  };

  const saveReflective = (v: boolean) => {
    setReflectiveDone(v);
    localStorage.setItem(STORAGE_KEY + "_reflective", String(v));
  };

  const saveRenewal = () => {
    const d = new Date(renewalInput);
    setRenewalDate(d);
    localStorage.setItem(STORAGE_KEY + "_renewal", d.toISOString());
  };

  const daysUntil = getDaysUntil(renewalDate);
  const cpdMet = cpdCount >= 9;
  const allMet = cpdMet && peerDone && reflectiveDone;

  return (
    <PharmacistLayout title="GPhC Revalidation Dashboard">
      <div className="space-y-8 max-w-4xl">

        {/* Header */}
        <div className="bg-gradient-to-br from-violet-900 to-violet-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-violet-300" />
            <div>
              <h2 className="text-xl font-bold">GPhC Revalidation</h2>
              <p className="text-violet-300 text-sm">Annual registration renewal · GPhC Standards for Pharmacy Professionals</p>
            </div>
          </div>
          {allMet ? (
            <div className="mt-3 bg-emerald-500/20 border border-emerald-400/30 rounded-xl px-4 py-3 text-emerald-200 text-sm flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-300 shrink-0" />
              All three revalidation requirements met for this cycle. Keep up the good work!
            </div>
          ) : (
            <div className="mt-3 bg-white/10 rounded-xl px-4 py-3 text-violet-200 text-sm flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-300 shrink-0" />
              Some revalidation requirements still to complete for this cycle.
            </div>
          )}
        </div>

        {/* Renewal countdown */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Registration Renewal Date</h3>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label htmlFor="renewal-date" className="block text-xs font-medium text-slate-600 mb-1.5">My GPhC renewal date</label>
              <input
                id="renewal-date"
                type="date"
                value={renewalInput}
                onChange={(e) => setRenewalInput(e.target.value)}
                className="h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <button
              onClick={saveRenewal}
              className="h-9 px-4 bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Save
            </button>
            <div className={`px-4 py-2 rounded-xl text-sm font-semibold ${daysUntil <= 30 ? "bg-red-50 text-red-700" : daysUntil <= 90 ? "bg-amber-50 text-amber-700" : "bg-violet-50 text-violet-700"}`}>
              {daysUntil > 0 ? `${daysUntil} days until renewal` : "Renewal date has passed"}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">GPhC registration renewal runs annually. You must submit revalidation evidence before your renewal date.</p>
        </div>

        {/* Revalidation checklist */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-1">Annual Revalidation Checklist</h3>
          <p className="text-sm text-slate-500 mb-5">GPhC requires all pharmacists to submit three pieces of evidence each year:</p>
          <div className="space-y-4">

            {/* CPD entries */}
            <div className={`flex items-start gap-4 p-4 rounded-xl border ${cpdMet ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
              <div className={`mt-0.5 ${cpdMet ? "text-emerald-600" : "text-slate-400"}`}>
                {cpdMet ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900 text-sm">CPD entries (minimum 9 per year)</p>
                <p className="text-xs text-slate-500 mt-0.5">Record at least 9 CPD entries using the GPhC cycle (Reflect, Plan, Act, Evaluate). Each must demonstrate benefit to patients or the public.</p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 bg-white rounded-full h-1.5 border border-slate-200">
                    <div
                      className={`h-1.5 rounded-full transition-all ${cpdMet ? "bg-emerald-500" : "bg-violet-500"}`}
                      style={{ width: `${Math.min((cpdCount / 9) * 100, 100)}%` }}
                    />
                  </div>
                  <span className={`text-sm font-bold ${cpdMet ? "text-emerald-700" : "text-violet-700"}`}>{cpdCount}/9</span>
                </div>
              </div>
            </div>

            {/* Peer discussion */}
            <div className={`flex items-start gap-4 p-4 rounded-xl border ${peerDone ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
              <button
                onClick={() => savePeer(!peerDone)}
                className={`mt-0.5 ${peerDone ? "text-emerald-600" : "text-slate-400 hover:text-slate-600"}`}
              >
                {peerDone ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
              </button>
              <div className="flex-1">
                <p className="font-semibold text-slate-900 text-sm">Peer discussion (1 per year)</p>
                <p className="text-xs text-slate-500 mt-0.5">A structured discussion with a peer about your practice and development. The peer must be a registered healthcare professional but does not need to be a pharmacist.</p>
                <p className="text-xs text-slate-400 mt-1">Click the circle to mark as complete.</p>
              </div>
            </div>

            {/* Reflective account */}
            <div className={`flex items-start gap-4 p-4 rounded-xl border ${reflectiveDone ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
              <button
                onClick={() => saveReflective(!reflectiveDone)}
                className={`mt-0.5 ${reflectiveDone ? "text-emerald-600" : "text-slate-400 hover:text-slate-600"}`}
              >
                {reflectiveDone ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
              </button>
              <div className="flex-1">
                <p className="font-semibold text-slate-900 text-sm">Reflective account (1 per year)</p>
                <p className="text-xs text-slate-500 mt-0.5">A written reflection on how you have applied the GPhC Standards for Pharmacy Professionals to your practice during the year.</p>
                <p className="text-xs text-slate-400 mt-1">Click the circle to mark as complete.</p>
              </div>
            </div>

          </div>
        </div>

        {/* GPhC Standards reference */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-1">GPhC Standards for Pharmacy Professionals</h3>
          <p className="text-sm text-slate-500 mb-4">Nine standards that set out what it means to be a pharmacy professional in Great Britain. Your reflective account should reference these standards.</p>
          <div className="space-y-2">
            {GPHC_STANDARDS.map((s) => {
              const open = expandedStd === s.num;
              return (
                <div key={s.num} className="border border-slate-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedStd(open ? null : s.num)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="flex-shrink-0 h-6 w-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">
                      {s.num}
                    </span>
                    <span className="flex-1 text-sm font-medium text-slate-900">{s.title}</span>
                    {open ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                  </button>
                  {open && (
                    <div className="px-4 pb-4 pt-0 text-sm text-slate-600 border-t border-slate-100">
                      {s.desc}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 mt-4">Source: GPhC Standards for Pharmacy Professionals (2017, updated). See <a href="https://www.pharmacyregulation.org/standards/standards-for-pharmacy-professionals" target="_blank" rel="noopener noreferrer" className="underline hover:text-violet-600">pharmacyregulation.org</a> for the full standards.</p>
        </div>

      </div>
    </PharmacistLayout>
  );
}
